import { useCallback, useEffect, useRef, useState } from "react";

const SHOW_OWNERS_KEY = "constellation.showOwners";

function readShowOwners(): boolean {
    try {
        return localStorage.getItem(SHOW_OWNERS_KEY) !== "false"; // default on
    } catch {
        return true;
    }
}

export interface BoardView {
    showOwners: boolean;
    toggleOwners: () => void;
    // Bump to re-frame the camera on all placed content.
    fitRequest: number;
    requestFit: () => void;
    // Immersive board mode. The base layer is a CSS fixed-overlay (works on
    // load, keeps modals stacking on top). Clicking the fullscreen control also
    // requests the native OS Fullscreen API on `containerRef` (the click is the
    // gesture browsers require); `nativeFullscreen` reflects whether it is on.
    immersive: boolean;
    nativeFullscreen: boolean;
    containerRef: React.RefObject<HTMLElement | null>;
    enterImmersive: () => void;
    exitImmersive: () => void;
    toggleImmersive: () => void;
    // Toggle native OS fullscreen on the container (from a user gesture).
    toggleNativeFullscreen: () => void;
    // Drop out of native OS fullscreen while staying in the CSS-immersive layer,
    // so a portalled modal (figure picker, save-scene) is visible. Call before
    // opening any overlay from within the immersive board.
    dropNativeFullscreen: () => void;
}

/*
 * Framed inside a call, fullscreen is the call's to give: the board's own
 * fullscreen would fill the screen with the frame alone and hide the faces
 * beside it. The page asks the call instead, which puts the board and the
 * faces up together and says back whether it did. Kept in step with
 * `TOOL_BOARD_FULLSCREEN_*` in the session module, which answers.
 */
const MEETING_BOARD_FULLSCREEN = "alchely:meeting-board-fullscreen";
const MEETING_BOARD_FULLSCREEN_STATE = "alchely:meeting-board-fullscreen-state";

function isFramed(): boolean {
    return typeof window !== "undefined" && window.parent !== window;
}

function askCallForFullscreen(on: boolean): void {
    window.parent.postMessage({ type: MEETING_BOARD_FULLSCREEN, on }, window.location.origin);
}

function requestNative(el: HTMLElement | null): void {
    if (isFramed()) {
        askCallForFullscreen(true);
        return;
    }
    if (el?.requestFullscreen) {
        el.requestFullscreen().catch(() => {
            /* user gesture missing or unsupported — CSS immersive still applies */
        });
    }
}

function exitNative(): void {
    if (isFramed()) {
        askCallForFullscreen(false);
        return;
    }
    if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {
            /* ignore */
        });
    }
}

/** Shared board-view controls (owner labels, fit-to-board, immersive + native
 *  fullscreen) used by both the therapist and patient pages (issue #197). */
export function useBoardView(options?: { immersiveByDefault?: boolean }): BoardView {
    const [showOwners, setShowOwners] = useState<boolean>(readShowOwners);
    const [fitRequest, setFitRequest] = useState(0);
    const [immersive, setImmersive] = useState<boolean>(!!options?.immersiveByDefault);
    const [nativeFullscreen, setNativeFullscreen] = useState(false);
    // Read by the toggle, which is built once: framed, the call's answer is
    // the only way to know whether it is up.
    const nativeFullscreenRef = useRef(false);
    const containerRef = useRef<HTMLElement | null>(null);

    const toggleOwners = useCallback(() => {
        setShowOwners((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(SHOW_OWNERS_KEY, String(next));
            } catch {
                /* ignore */
            }
            return next;
        });
    }, []);

    const requestFit = useCallback(() => setFitRequest((n) => n + 1), []);

    const enterImmersive = useCallback(() => {
        setImmersive(true);
        // Escalate to native OS fullscreen. Succeeds when called from a user
        // gesture (a button click); silently no-ops otherwise.
        requestNative(containerRef.current);
    }, []);

    const exitImmersive = useCallback(() => {
        exitNative();
        setImmersive(false);
    }, []);

    const toggleImmersive = useCallback(() => {
        if (immersive) exitImmersive();
        else enterImmersive();
    }, [immersive, enterImmersive, exitImmersive]);

    const toggleNativeFullscreen = useCallback(() => {
        if (isFramed() ? nativeFullscreenRef.current : document.fullscreenElement) exitNative();
        else {
            setImmersive(true);
            requestNative(containerRef.current);
        }
    }, []);

    // Framed, the call's fullscreen holds this whole page, so a portalled
    // modal is already on screen and there is nothing to drop.
    const dropNativeFullscreen = useCallback(() => {
        if (!isFramed()) exitNative();
    }, []);

    // Keep native-fullscreen state in sync (covers Esc / browser-driven exits).
    // Leaving OS fullscreen keeps the CSS-immersive layer so the board stays
    // filled and the exit control remains reachable.
    useEffect(() => {
        if (isFramed()) {
            const onMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin || event.source !== window.parent) return;
                const data = event.data as { type?: unknown; active?: unknown } | null;
                if (data?.type !== MEETING_BOARD_FULLSCREEN_STATE) return;
                nativeFullscreenRef.current = data.active === true;
                setNativeFullscreen(data.active === true);
            };
            window.addEventListener("message", onMessage);
            return () => window.removeEventListener("message", onMessage);
        }
        const onChange = () => setNativeFullscreen(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", onChange);
        return () => document.removeEventListener("fullscreenchange", onChange);
    }, []);

    return {
        showOwners,
        toggleOwners,
        fitRequest,
        requestFit,
        immersive,
        nativeFullscreen,
        containerRef,
        enterImmersive,
        exitImmersive,
        toggleImmersive,
        toggleNativeFullscreen,
        dropNativeFullscreen,
    };
}
